/**
 * Inspection report (*procès-verbal / proces-verbaal* of the approved body) → its header and
 * the list of remarks to fix. The model only transcribes: each remark becomes a task the
 * electrician reviews before it is added to the project.
 */
import { z } from 'zod';
import { newId, type Inspection, type Op, type Remark } from '@elec/domain';

/** Report pages sent at once (photos of a paper report, or pages of its PDF). */
export const MAX_REPORT_PAGES = 6;

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable()
  .describe('YYYY-MM-DD, null if not on the report');

export const inspectionReportSchema = z.object({
  body: z.string().nullable().describe('Approved body that made the inspection (e.g. Vinçotte, BTV, ACEG)'),
  reference: z.string().nullable().describe('Report number'),
  date: isoDate.describe('Date of the visit, YYYY-MM-DD'),
  inspector: z.string().nullable().describe('Name of the inspector (agent visiteur / keurder)'),
  result: z
    .enum(['conform', 'not-conform', 'unknown'])
    .describe('Outcome ticked or written on the report; unknown if not readable'),
  deadline: isoDate.describe('Deadline for the remedial works, YYYY-MM-DD'),
  remarks: z
    .array(
      z.object({
        text: z.string().describe('The remark, copied verbatim in the language of the report'),
        article: z.string().nullable().describe('Article cited, e.g. "art. 86" or "8.2.1"'),
        ref: z
          .string()
          .nullable()
          .describe('Circuit letter (e.g. "B") or point code (e.g. "B3") when the remark names one'),
      }),
    )
    .describe('Every infraction or remark listed, in report order'),
  notes: z.string().describe('What was unreadable or uncertain'),
});
export type InspectionReport = z.infer<typeof inspectionReportSchema>;

export const INSPECTION_SYSTEM_PROMPT = `You read Belgian electrical inspection reports (procès-verbal / proces-verbaal
of an approved body: Vinçotte, BTV, ACEG, OCB, Electro-Test, AIB…; RGIE/AREI) for the electrician who
has to fix what the inspector found.

Rules:
- Transcribe; do not interpret. Copy each remark verbatim, in the report's language (French or Dutch).
- One entry per remark or infraction, in the order of the report. Skip boilerplate and legal notices.
- Give the article only when the report cites one next to the remark.
- Give ref only when the remark names a circuit (letter) or a point (letter + number).
- Dates as YYYY-MM-DD. "Conforme / gelijkvormig" → conform; "non conforme / niet gelijkvormig" → not-conform.
- Use null for anything that is not on the report. Never invent a remark.
- Use the notes field for unreadable parts.`;

export const INSPECTION_USER_PROMPT =
  'Read this inspection report and return its header and every remark with the inspection_report tool.';

/** What demo mode returns for any report: a typical "not compliant" Belgian report. */
export const DEMO_REPORT: InspectionReport = {
  body: 'Organisme agréé (démo)',
  reference: 'PV-2026-000123',
  date: '2026-10-15',
  inspector: 'J. Dupont',
  result: 'not-conform',
  deadline: '2027-10-15',
  remarks: [
    {
      text: 'Le schéma unifilaire ne correspond pas à l’installation (circuit C).',
      article: 'art. 16',
      ref: 'C',
    },
    {
      text: 'Absence de dispositif différentiel 30 mA pour le circuit de la salle de bains.',
      article: 'art. 86',
      ref: 'B',
    },
    { text: 'Prise de courant sans contact de terre.', article: 'art. 49', ref: 'A3' },
    { text: 'Ouvertures non obturées dans le tableau de répartition.', article: 'art. 49', ref: null },
  ],
  notes:
    'Démo : procès-verbal d’exemple, le document n’a pas été lu. / Demo: sample report, the document was not read.',
};

/** Ops adding a reviewed report and its remarks to the project. */
export function reportToOps(
  report: InspectionReport,
  pages: string[],
  keep: readonly boolean[] = report.remarks.map(() => true),
): { ops: Op[]; inspectionId: string } {
  const inspection: Inspection = {
    id: newId(),
    date: report.date ?? new Date().toISOString().slice(0, 10),
    body: report.body ?? '',
    reference: report.reference ?? '',
    inspector: report.inspector ?? '',
    result: report.result,
    ...(report.deadline ? { deadline: report.deadline } : {}),
    pages,
    notes: '',
  };
  const ops: Op[] = [{ op: 'add', coll: 'inspections', value: inspection }];
  let order = 0;
  report.remarks.forEach((r, i) => {
    if (!keep[i] || !r.text.trim()) return;
    const remark: Remark = {
      id: newId(),
      inspectionId: inspection.id,
      order: order++,
      text: r.text.trim(),
      article: r.article?.trim() ?? '',
      ref: r.ref?.trim().toUpperCase() ?? '',
      status: 'open',
      fixNote: '',
      photos: [],
    };
    ops.push({ op: 'add', coll: 'remarks', value: remark });
  });
  return { ops, inspectionId: inspection.id };
}
